<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';

const props = defineProps({
  resources: { type: Object, default: () => ({}) },
});

const { locale, t } = useI18n();
const list = key =>
  Array.isArray(props.resources[key]) ? props.resources[key] : [];
const paymentMethods = computed(() => list('payment_methods'));
const financialEvents = computed(() => [
  ...list('disputes').map(item => ({ ...item, resourceType: 'dispute' })),
  ...list('refunds').map(item => ({ ...item, resourceType: 'refund' })),
]);
const subscriptions = computed(() => list('bubble_subscriptions'));
const subscriptionEvents = computed(() => list('subscription_events'));
const catalog = computed(() => list('products'));
const hasData = computed(
  () =>
    paymentMethods.value.length ||
    financialEvents.value.length ||
    subscriptions.value.length ||
    subscriptionEvents.value.length ||
    catalog.value.length
);
const financialHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.TYPE'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.REFERENCE'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.AMOUNT'),
]);
const subscriptionHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SERVER'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.CONTAINER'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUBSCRIPTION'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
]);
const eventHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EVENT'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUBSCRIPTION'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UPDATED_AT'),
]);

const resourceTypeLabels = computed(() => ({
  dispute: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_TYPE.dispute'),
  refund: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_TYPE.refund'),
}));
const resourceTypeLabel = type =>
  resourceTypeLabels.value[type] ||
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.unknown');
const paymentMethodLabel = method =>
  method.last4
    ? t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.CARD_ENDING', {
        brand:
          method.brand || t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.PAYMENT_METHOD'),
        last4: method.last4,
      })
    : method.brand || t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.PAYMENT_METHOD');
const resourceStatusLabels = computed(() => ({
  active: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.active'),
  inactive: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.inactive'),
  canceled: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.canceled'),
  won: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.won'),
  lost: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.lost'),
  warning_needs_response: t(
    'INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.warning_needs_response'
  ),
  under_review: t(
    'INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.under_review'
  ),
  succeeded: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.succeeded'),
  failed: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.failed'),
  pending: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.pending'),
  unknown: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.RESOURCE_STATUS.unknown'),
}));
const resourceStatusLabel = status => {
  const normalized = String(status || '').toLowerCase();
  return (
    resourceStatusLabels.value[normalized] || resourceStatusLabels.value.unknown
  );
};
const formatMoney = (amount, currency = 'BRL') => {
  const numericAmount = Number(amount || 0);
  try {
    return new Intl.NumberFormat(locale.value.replace('_', '-'), {
      style: 'currency',
      currency: String(currency || 'BRL').toUpperCase(),
    }).format(numericAmount / 100);
  } catch {
    return `${numericAmount / 100} ${String(currency || 'BRL').toUpperCase()}`;
  }
};
const formatDate = value => {
  if (!value) return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE');
  const date = new Date(typeof value === 'number' ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) {
    return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE');
  }
  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <section
    v-if="hasData"
    class="flex min-w-0 flex-col gap-4 border-t border-n-weak pt-4"
  >
    <h3 class="m-0 text-heading-3 text-n-slate-12">
      {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUPPORTING_DETAILS') }}
    </h3>

    <div v-if="paymentMethods.length" class="flex min-w-0 flex-col gap-2">
      <h4 class="m-0 text-label-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.PAYMENT_METHODS') }}
      </h4>
      <div class="grid min-w-0 gap-2 sm:grid-cols-2">
        <div
          v-for="method in paymentMethods"
          :key="method.id"
          class="rounded-lg border border-n-weak p-3"
        >
          <p class="m-0 font-medium text-n-slate-12">
            {{ paymentMethodLabel(method) }}
          </p>
          <p class="m-0 mt-1 text-label-small leading-4 text-n-slate-11">
            {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EXPIRATION') }}
            {{
              method.exp_month && method.exp_year
                ? `${method.exp_month}/${method.exp_year}`
                : $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE')
            }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="financialEvents.length" class="min-w-0 overflow-x-auto">
      <h4 class="m-0 mb-2 text-label-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.DISPUTES_REFUNDS') }}
      </h4>
      <BaseTable :headers="financialHeaders" :items="financialEvents">
        <template #row="{ items }">
          <BaseTableRow v-for="item in items" :key="item.id" :item="item">
            <BaseTableCell>
              {{ resourceTypeLabel(item.resourceType) }}
            </BaseTableCell>
            <BaseTableCell>{{ item.id }}</BaseTableCell>
            <BaseTableCell>
              {{ resourceStatusLabel(item.status) }}
            </BaseTableCell>
            <BaseTableCell>
              {{ formatMoney(item.amount, item.currency) }}
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>

    <div v-if="subscriptions.length" class="min-w-0 overflow-x-auto">
      <h4 class="m-0 mb-2 text-label-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUBSCRIPTIONS') }}
      </h4>
      <BaseTable :headers="subscriptionHeaders" :items="subscriptions">
        <template #row="{ items }">
          <BaseTableRow v-for="item in items" :key="item.id" :item="item">
            <BaseTableCell>{{ item.name || item.url }}</BaseTableCell>
            <BaseTableCell>{{ item.container_name }}</BaseTableCell>
            <BaseTableCell>{{ item.stripe_subscription_id }}</BaseTableCell>
            <BaseTableCell>
              {{ resourceStatusLabel(item.license_status) }}
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>

    <div v-if="subscriptionEvents.length" class="min-w-0 overflow-x-auto">
      <h4 class="m-0 mb-2 text-label-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUBSCRIPTION_EVENTS') }}
      </h4>
      <BaseTable :headers="eventHeaders" :items="subscriptionEvents">
        <template #row="{ items }">
          <BaseTableRow v-for="item in items" :key="item.id" :item="item">
            <BaseTableCell>{{ item.id }}</BaseTableCell>
            <BaseTableCell>{{ item.subscription_id }}</BaseTableCell>
            <BaseTableCell>
              {{ resourceStatusLabel(item.status) }}
            </BaseTableCell>
            <BaseTableCell>{{ formatDate(item.created) }}</BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>

    <div v-if="catalog.length" class="min-w-0">
      <h4 class="m-0 mb-2 text-label-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.CATALOG') }}
      </h4>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="product in catalog"
          :key="product.id"
          class="rounded-lg border border-n-weak px-3 py-2 text-label-small leading-4 text-n-slate-12"
        >
          {{ product.name || product.id }}
        </span>
      </div>
    </div>
  </section>
</template>
